import type { GameFlow, GameFlowSnapshot } from "./game-flow";
import type { PlayerRuntime } from "./player-runtime";
import { getModeConfig } from "../config/gameplay-config";
import {
  advanceRound,
  createRoundState,
  requestRoundEnd,
  type RoundEndReason,
  type RoundState,
} from "../simulation/round-state";

export type RoundRuntimeSnapshot = RoundState;
export type RoundRuntimeListener = (snapshot: RoundRuntimeSnapshot) => void;

type RoundPlayerPort = Pick<
  PlayerRuntime,
  "endRound" | "subscribeFixedStepCompletion"
>;

const durationFor = (snapshot: GameFlowSnapshot): number | null => {
  if (snapshot.scene !== "gameplay" || snapshot.mode === null) {
    return null;
  }
  return getModeConfig(snapshot.mode).roundDurationMs;
};

export class RoundRuntime {
  private readonly listeners = new Set<RoundRuntimeListener>();
  private state = createRoundState(null);

  public constructor(
    flow: GameFlow,
    private readonly player: RoundPlayerPort,
  ) {
    player.subscribeFixedStepCompletion((stepMilliseconds) => {
      this.advance(stepMilliseconds);
    });
    flow.subscribe((snapshot) => {
      if (snapshot.scene === "results") {
        return;
      }
      this.state = createRoundState(durationFor(snapshot));
      this.notify();
    });
  }

  public endRound(reason: RoundEndReason): boolean {
    const transition = requestRoundEnd(this.state, reason);
    if (!transition.accepted || !this.player.endRound()) {
      return false;
    }
    this.state = transition.state;
    this.notify();
    return true;
  }

  public getSnapshot(): RoundRuntimeSnapshot {
    return { ...this.state };
  }

  public subscribe(listener: RoundRuntimeListener): () => void {
    this.listeners.add(listener);
    listener(this.getSnapshot());
    return () => {
      this.listeners.delete(listener);
    };
  }

  private advance(stepMilliseconds: number): void {
    const next = advanceRound(this.state, stepMilliseconds);
    if (next === this.state) {
      return;
    }
    if (next.result !== null && !this.player.endRound()) {
      return;
    }
    this.state = next;
    this.notify();
  }

  private notify(): void {
    for (const listener of this.listeners) {
      listener(this.getSnapshot());
    }
  }
}
